// Agent launcher - the shared machinery behind "start a conversation with an
// agent" from anywhere (the QuickAsk popover chips, the Studio empty state,
// the Conversations "New thread" row). Keeps one cached list of enabled agents
// and a one-shot prompt handoff so a launcher can create the conversation,
// route to Studio, and have Studio auto-send the first message on arrival.
//
// Why a store and not per-view fetches: the chip row appears in 3 places and
// the agent list rarely changes; caching it once avoids 3 redundant /v1/agents
// round-trips and keeps the chips identical everywhere.

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/api/client';

export interface LaunchAgent {
  id:           string;
  slug:         string;
  display_name: string;
  description:  string | null;
  avatar_url:   string | null;
  is_default:   boolean;
  status:       string;
}

export const useLaunchStore = defineStore('launch', () => {
  const agents  = ref<LaunchAgent[]>([]);
  const loaded  = ref(false);
  const loading = ref(false);
  const creating = ref(false);

  // One-shot first-message handoff, keyed by conversation id. The launcher
  // arms it just before routing to Studio; StudioView.take()s it once on
  // mount and auto-sends, so the user lands mid-turn instead of on a blank
  // composer. Keyed so a stale arm can never fire on the wrong conversation.
  const pending = ref<{ id: string; text: string; files: File[] } | null>(null);

  // Enabled agents, default first (so Max leads), then by name. Capped at the
  // call site; the overflow goes to the full picker.
  const enabled = computed(() =>
    agents.value
      .filter((a) => a.status === 'enabled')
      .slice()
      .sort((a, b) =>
        Number(b.is_default) - Number(a.is_default) ||
        a.display_name.localeCompare(b.display_name),
      ),
  );

  async function loadAgents(force = false): Promise<void> {
    if ((loaded.value || loading.value) && !force) return;
    loading.value = true;
    try {
      const resp = await api.get<{ agents: LaunchAgent[] }>('/v1/agents');
      agents.value = resp.agents;
      loaded.value = true;
    } catch {
      // Leave the list empty - the launcher just falls back to fast-only.
    } finally {
      loading.value = false;
    }
  }

  /** Create a fresh agent conversation and return its id. Heavy: this is the
   *  microVM path. Call only on an explicit send/click, never on hover. */
  async function createConversation(agentId: string): Promise<string> {
    creating.value = true;
    try {
      const conv = await api.post<{ id: string }>(
        '/v1/api/conversations',
        { agent_id: agentId },
      );
      return conv.id;
    } finally {
      creating.value = false;
    }
  }

  // Arm the first message (text + any staged attachments) for a conversation
  // we're about to route to. Files ride along as in-memory File objects (the
  // client-side route push keeps them alive); Studio stages + uploads them on
  // its auto-send. Armed when there's text OR files - a file-only first message
  // is valid.
  function armPrompt(id: string, text: string, files: File[] = []): void {
    const t = text.trim();
    pending.value = (t || files.length) ? { id, text: t, files } : null;
  }

  /** Consume the armed first message for `id` (returns + clears), or null. */
  function takePrompt(id: string): { text: string; files: File[] } | null {
    if (pending.value && pending.value.id === id) {
      const { text, files } = pending.value;
      pending.value = null;
      return { text, files };
    }
    return null;
  }

  return {
    agents, enabled, loaded, loading, creating, pending,
    loadAgents, createConversation, armPrompt, takePrompt,
  };
});
